import { cn } from "../../lib/utils.ts";
import { DrinkIcon, IconCheers } from "./icons.tsx";

export type DrinkTicketStatus = "pending" | "accepted" | "declined" | "expired";

export interface DrinkTicketCardProps {
  drinkName: string;
  drinkNameTh?: string;
  /** Menu icon name, e.g. "IconBeer". */
  iconName?: string;
  /** Pre-formatted price, e.g. "฿120". */
  price?: string;
  /** "From" label — usually the sender's table. */
  fromLabel: string;
  /** "To" label — the recipient table. */
  toLabel: string;
  message?: string;
  status?: DrinkTicketStatus;
  /** Play the lift-in entrance. */
  animated?: boolean;
  className?: string;
}

const STATUS_COPY: Record<
  DrinkTicketStatus,
  { th: string; tone: "gold" | "lime" | "muted" | "danger" }
> = {
  pending: { th: "รอเขากดรับ", tone: "gold" },
  accepted: { th: "รับแล้ว · กำลังเสิร์ฟ", tone: "lime" },
  declined: { th: "ปฏิเสธอย่างนุ่มนวล", tone: "muted" },
  expired: { th: "หมดเวลา", tone: "muted" },
};

/**
 * The signature "Just One Drink" card — a perforated ticket/coaster with a
 * from→to lockup and a cheers seal. The product's most recognizable object.
 */
export function DrinkTicketCard({
  drinkName,
  drinkNameTh,
  iconName = "IconCocktail",
  price,
  fromLabel,
  toLabel,
  message,
  status = "pending",
  animated,
  className,
}: DrinkTicketCardProps) {
  const statusInfo = STATUS_COPY[status];
  const statusColor =
    statusInfo.tone === "lime"
      ? "text-lime"
      : statusInfo.tone === "danger"
        ? "text-destructive"
        : statusInfo.tone === "muted"
          ? "text-cream-3"
          : "text-gold";

  return (
    <div
      className={cn(
        "glass-hi relative overflow-hidden rounded-xl px-5 py-5",
        animated && "jod-card-lift",
        className,
      )}
      data-testid="drink-ticket-card"
    >
      <div className="jod-sheen" aria-hidden="true" />

      {/* from → to lockup */}
      <div className="relative flex items-center justify-between text-[0.78rem]">
        <div className="min-w-0">
          <div className="jod-kicker">จาก</div>
          <div className="truncate font-display font-semibold text-cream">{fromLabel}</div>
        </div>
        <div className="mx-3 flex shrink-0 items-center gap-1.5 text-gold">
          <span className="h-px w-5 bg-[var(--hairline-strong)]" />
          <IconCheers size={20} />
          <span className="h-px w-5 bg-[var(--hairline-strong)]" />
        </div>
        <div className="min-w-0 text-right">
          <div className="jod-kicker">ถึง</div>
          <div className="truncate font-display font-semibold text-cream">{toLabel}</div>
        </div>
      </div>

      {/* perforation tear-line with side notches */}
      <div className="relative my-4">
        <span className="jod-notch" style={{ left: -31 }} aria-hidden="true" />
        <span className="jod-notch" style={{ right: -31 }} aria-hidden="true" />
        <div className="border-[var(--hairline-strong)] border-t border-dashed" />
      </div>

      {/* the drink */}
      <div className="relative flex items-center gap-3.5">
        <div className="grid size-14 shrink-0 place-items-center rounded-lg bg-[oklch(100%_0_0_/_5%)] text-gold">
          <DrinkIcon name={iconName} size={30} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display font-semibold text-[1.05rem] text-cream">
            {drinkName}
          </div>
          {drinkNameTh ? <div className="truncate text-cream-3 text-xs">{drinkNameTh}</div> : null}
        </div>
        {price ? (
          <div className="shrink-0 font-display font-semibold text-gold">{price}</div>
        ) : null}
      </div>

      {message ? (
        <p className="relative mt-3.5 rounded-md bg-[oklch(100%_0_0_/_4%)] px-3.5 py-2.5 text-cream-2 text-sm leading-relaxed">
          “{message}”
        </p>
      ) : null}

      <div
        className={cn(
          "relative mt-3.5 flex items-center gap-2 font-medium text-[0.8rem]",
          statusColor,
        )}
      >
        <span className="jod-live-dot" style={{ background: "currentColor" }} aria-hidden="true" />
        {statusInfo.th}
      </div>
    </div>
  );
}
